import { createSlice, PayloadAction } from "@reduxjs/toolkit";

interface RecipientData {
  email: string;
  name?: string;
  phone?: string;
}

interface SurveyDistributionState {
  isOpen: boolean;
  surveyId?: string;
  surveyTitle?: string;
  recipients: RecipientData[];
  selectedTemplateId?: string;
  isSending: boolean;
  sendProgress: {
    total: number;
    sent: number;
    failed: number;
  };
  csvContent?: string;
  recipientValidationErrors: Array<{ row: number; email: string; error: string }>;
}

const initialState: SurveyDistributionState = {
  isOpen: false,
  recipients: [],
  isSending: false,
  sendProgress: {
    total: 0,
    sent: 0,
    failed: 0,
  },
  recipientValidationErrors: [],
};

const surveyDistributionSlice = createSlice({
  name: "surveyDistribution",
  initialState,
  reducers: {
    // ============================================
    // Dialog Management
    // ============================================

    openDistributionDialog: (
      state,
      action: PayloadAction<{ surveyId: string; surveyTitle: string }>,
    ) => {
      state.isOpen = true;
      state.surveyId = action.payload.surveyId;
      state.surveyTitle = action.payload.surveyTitle;
      state.recipients = [];
      state.selectedTemplateId = undefined;
      state.isSending = false;
      state.sendProgress = { total: 0, sent: 0, failed: 0 };
      state.csvContent = undefined;
      state.recipientValidationErrors = [];
    },

    closeDistributionDialog: (state) => {
      state.isOpen = false;
      state.surveyId = undefined;
      state.surveyTitle = undefined;
      state.recipients = [];
      state.selectedTemplateId = undefined;
      state.isSending = false;
      state.sendProgress = { total: 0, sent: 0, failed: 0 };
      state.csvContent = undefined;
      state.recipientValidationErrors = [];
    },

    // ============================================
    // Recipient Management
    // ============================================

    addRecipient: (state, action: PayloadAction<RecipientData>) => {
      state.recipients.push(action.payload);
    },

    updateRecipient: (state, action: PayloadAction<{ index: number; data: RecipientData }>) => {
      const { index, data } = action.payload;
      if (index >= 0 && index < state.recipients.length) {
        state.recipients[index] = data;
      }
    },

    removeRecipient: (state, action: PayloadAction<number>) => {
      const index = action.payload;
      if (index >= 0 && index < state.recipients.length) {
        state.recipients.splice(index, 1);
      }
    },

    setRecipients: (state, action: PayloadAction<RecipientData[]>) => {
      state.recipients = action.payload;
    },

    clearRecipients: (state) => {
      state.recipients = [];
    },

    // ============================================
    // CSV Upload & Parsing
    // ============================================

    setCsvContent: (state, action: PayloadAction<string>) => {
      state.csvContent = action.payload;
    },

    setRecipientValidationErrors: (
      state,
      action: PayloadAction<Array<{ row: number; email: string; error: string }>>,
    ) => {
      state.recipientValidationErrors = action.payload;
    },

    clearValidationErrors: (state) => {
      state.recipientValidationErrors = [];
    },

    // ============================================
    // Template Selection
    // ============================================

    selectTemplate: (state, action: PayloadAction<string | undefined>) => {
      state.selectedTemplateId = action.payload;
    },

    // ============================================
    // Send Progress
    // ============================================

    startSending: (state) => {
      state.isSending = true;
      state.sendProgress = {
        total: state.recipients.length,
        sent: 0,
        failed: 0,
      };
    },

    updateSendProgress: (state, action: PayloadAction<{ sent: number; failed: number }>) => {
      const { sent, failed } = action.payload;
      state.sendProgress.sent = sent;
      state.sendProgress.failed = failed;
    },

    completeSending: (state) => {
      state.isSending = false;
    },

    resetSendProgress: (state) => {
      state.sendProgress = {
        total: 0,
        sent: 0,
        failed: 0,
      };
    },
  },
});

export const {
  openDistributionDialog,
  closeDistributionDialog,
  addRecipient,
  updateRecipient,
  removeRecipient,
  setRecipients,
  clearRecipients,
  setCsvContent,
  setRecipientValidationErrors,
  clearValidationErrors,
  selectTemplate,
  startSending,
  updateSendProgress,
  completeSending,
  resetSendProgress,
} = surveyDistributionSlice.actions;

export default surveyDistributionSlice.reducer;
